import { useState, useEffect, useRef } from 'react'
import {
  Drawer, Box, Text, Badge, Group, Button,
  NumberInput, TextInput, ActionIcon, Popover, Stack
} from '@mantine/core'
import { IoTrashOutline } from 'react-icons/io5'
import { toast } from 'react-toastify'
import agent from '../../app/api/agent'
import type { BuildPartDto } from '../../app/models/build'
import RailPositionSelector from './RailPositionSelector'
import { RAIL_MOUNTABLE_CATEGORIES } from './partDefaults'

interface Props {
  buildId: string;
  zone: string;
  parts: BuildPartDto[];
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
  /** Called when user clicks "+ Add Another Part"; parent swaps to AddPartPanel. */
  onAddAnother: () => void;
  currentMountType?: string | null;
  isAk?: boolean;
}

export default function ZoneManagePanel ({
  buildId, zone, parts, open, onClose, onUpdate, onAddAnother, currentMountType, isAk
}: Props) {
  const [localQty, setLocalQty] = useState<Record<string, number>>({})
  const [localNotes, setLocalNotes] = useState<Record<string, string>>({})
  const [localRail, setLocalRail] = useState<Record<string, string>>({})
  const [removeConfirm, setRemoveConfirm] = useState<string | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)
  const debounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  // Re-initialise local state when panel opens or a part is added/removed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLocalQty(Object.fromEntries(parts.map((bp) => [bp.id, bp.quantity])))
    setLocalNotes(Object.fromEntries(parts.map((bp) => [bp.id, bp.notes ?? ''])))
    setLocalRail(Object.fromEntries(parts.map((bp) => [bp.id, bp.railPosition ?? ''])))
  }, [open, parts.length])

  const handleQtyChange = (bp: BuildPartDto, qty: number) => {
    // Capture current notes at call-time to avoid stale closure in debounce.
    const currentNotes = localNotes[bp.id] ?? bp.notes ?? undefined
    setLocalQty((prev) => ({ ...prev, [bp.id]: qty }))
    clearTimeout(debounceRef.current[bp.id])
    debounceRef.current[bp.id] = setTimeout(async () => {
      try {
        await agent.Builds.updatePart(
          buildId, bp.id, currentNotes, qty, bp.zoneOverride ?? undefined
        )
        onUpdate()
      } catch { /* global interceptor */ }
    }, 600)
  }

  const handleNotesChange = (bp: BuildPartDto, notes: string) => {
    const currentQty = localQty[bp.id] ?? bp.quantity
    setLocalNotes((prev) => ({ ...prev, [bp.id]: notes }))
    clearTimeout(debounceRef.current[`notes-${bp.id}`])
    debounceRef.current[`notes-${bp.id}`] = setTimeout(async () => {
      try {
        await agent.Builds.updatePart(
          buildId, bp.id, notes || undefined, currentQty, bp.zoneOverride ?? undefined
        )
        onUpdate()
      } catch { /* global interceptor */ }
    }, 600)
  }

  const handleRailChange = (bp: BuildPartDto, rail: string) => {
    setLocalRail((prev) => ({ ...prev, [bp.id]: rail }))
    clearTimeout(debounceRef.current[`rail-${bp.id}`])
    debounceRef.current[`rail-${bp.id}`] = setTimeout(async () => {
      try {
        await agent.Builds.updatePart(
          buildId, bp.id, undefined, undefined, undefined,
          { railPosition: rail || null, clearRailPosition: !rail }
        )
        onUpdate()
      } catch { /* global interceptor */ }
    }, 600)
  }

  const handleRemove = async (bp: BuildPartDto) => {
    setRemoving(bp.id)
    try {
      // removePart takes the Part's ID (bp.partId), not the BuildPart record ID.
      await agent.Builds.removePart(buildId, bp.partId)
      toast.success(`${bp.part?.name ?? 'Part'} removed`)
      setRemoveConfirm(null)
      onUpdate()
    } catch { /* global interceptor */ } finally { setRemoving(null) }
  }

  return (
    <Drawer
      opened={open}
      onClose={onClose}
      title={
        <Group spacing={8} align="center">
          <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 18, color: 'var(--gs-text)' }}>
            {zone}
          </Text>
          <Badge size="sm" variant="outline" sx={{ borderColor: 'var(--gs-border-bright)', color: 'var(--gs-muted)' }}>
            {parts.length} {parts.length === 1 ? 'part' : 'parts'}
          </Badge>
        </Group>
      }
      position="right"
      size="md"
      padding="xl"
      styles={{
        drawer: { background: 'var(--gs-surface)', borderLeft: '1px solid var(--gs-border)' },
        header: { background: 'var(--gs-surface)', borderBottom: '1px solid var(--gs-border)', paddingBottom: 12 },
        title: { width: '100%' }
      }}
    >
      <Stack spacing="md">
        {parts.map((bp) => (
          <Box
            key={bp.id}
            sx={{
              background: 'var(--gs-surface2)',
              border: '1px solid var(--gs-border)',
              borderRadius: 8,
              padding: '12px 14px'
            }}
          >
            <Group position="apart" noWrap mb={8} align="flex-start">
              <Box sx={{ minWidth: 0 }}>
                <Text size="sm" weight={600} sx={{ color: 'var(--gs-text)' }} lineClamp={1}>
                  {bp.part?.name ?? 'Part'}
                </Text>
                {bp.part?.manufacturer && (
                  <Text size="xs" sx={{ color: 'var(--gs-muted)' }}>
                    {bp.part.manufacturer}
                  </Text>
                )}
              </Box>

              <Popover
                opened={removeConfirm === bp.id}
                onClose={() => setRemoveConfirm(null)}
                position="left"
                withArrow
              >
                <Popover.Target>
                  <ActionIcon
                    size="sm"
                    onClick={() => setRemoveConfirm(removeConfirm === bp.id ? null : bp.id)}
                    sx={{
                      flexShrink: 0,
                      color: 'var(--gs-muted)',
                      '&:hover': { color: 'var(--gs-red)' }
                    }}
                    title="Remove part"
                  >
                    <IoTrashOutline size={14} />
                  </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown
                  sx={{ background: 'var(--gs-surface2)', border: '1px solid var(--gs-border)' }}
                >
                  <Text size="xs" mb={8} sx={{ color: 'var(--gs-text)' }}>
                    Remove {bp.part?.name ?? 'this part'}?
                  </Text>
                  <Group spacing={6}>
                    <Button size="xs" variant="default" onClick={() => setRemoveConfirm(null)}>
                      Cancel
                    </Button>
                    <Button
                      size="xs"
                      color="red"
                      loading={removing === bp.id}
                      onClick={() => handleRemove(bp)}
                    >
                      Remove
                    </Button>
                  </Group>
                </Popover.Dropdown>
              </Popover>
            </Group>

            <Group grow spacing={8}>
              <NumberInput
                label="Qty"
                size="xs"
                min={1}
                max={99}
                value={localQty[bp.id] ?? bp.quantity}
                onChange={(v) => handleQtyChange(bp, typeof v === 'number' ? v : 1)}
                styles={{
                  input: {
                    background: 'var(--gs-surface)',
                    borderColor: 'var(--gs-border)',
                    color: 'var(--gs-text)'
                  }
                }}
              />
              <TextInput
                label="Notes"
                size="xs"
                placeholder="e.g. torqued to spec"
                value={localNotes[bp.id] ?? bp.notes ?? ''}
                onChange={(e) => handleNotesChange(bp, e.currentTarget.value)}
                styles={{
                  input: {
                    background: 'var(--gs-surface)',
                    borderColor: 'var(--gs-border)',
                    color: 'var(--gs-text)',
                    '&::placeholder': { color: 'var(--gs-muted)' }
                  }
                }}
              />
            </Group>

            {RAIL_MOUNTABLE_CATEGORIES.includes(bp.part?.category ?? '') && (
              <Box mt={10}>
                <RailPositionSelector
                  value={localRail[bp.id] ?? bp.railPosition ?? null}
                  onChange={(rail) => handleRailChange(bp, rail)}
                />
              </Box>
            )}
          </Box>
        ))}

        <Button
          variant="subtle"
          size="sm"
          onClick={onAddAnother}
          sx={{
            color: 'var(--gs-accent)',
            border: '1px dashed var(--gs-border)',
            '&:hover': { background: 'rgba(232,93,4,0.06)', borderColor: 'var(--gs-accent)' }
          }}
        >
          + Add Another Part
        </Button>
      </Stack>
    </Drawer>
  )
}
